import { useState, useEffect } from 'react';
import { Input, Button, message, TimePicker } from 'antd';
import { Formik, ErrorMessage } from 'formik';
import { useHistory } from 'react-router-dom';
import { T } from 'react-translator-component';
import moment from 'moment';
import * as S from './styled';
import * as H from './helpers';

import {
  createChargingProfile,
  updateChargingProfile,
} from '../../../hooks/profiles';

const handleSubmit = async (values, { setSubmitting }, type, id, history) => {
  setSubmitting(true);

  try {
    let data = null;

    if (type === 'create') {
      data = await createChargingProfile(values);
    } else if (type === 'update') {
      data = await updateChargingProfile(values, id);
    }

    if (data?.type === 'success') {
      message.success(
        type === 'create'
          ? T('Charging Profile created successfully')
          : T('Charging Profile updated successfully'),
      );
      setTimeout(() => history.goBack(), 1000);
    } else {
      message.error(
        T(
          'Unable to perform the operation. Make sure the fields are filled in correctly.',
        ),
      );
    }
  } catch (error) {
    message.error(
      T(
        'Unable to perform the operation. Make sure the fields are filled in correctly.',
      ),
    );
  } finally {
    setSubmitting(false);
  }
};

const ChargingProfileForm = ({ initialValues, type, id = null }) => {
  const history = useHistory();
  const [isIntervalValid, setIsIntervalValid] = useState(true);

  const [intervals, setIntervals] = useState(() =>
    initialValues.intervals?.length > 0
      ? initialValues.intervals.map((interval) => ({
          beginningEnd: interval.beginningEnd?.map((time) =>
            typeof time === 'string' ? time : time.format('HH:mm'),
          ),
          currentLimit: interval.currentLimit?.toString(),
        }))
      : [{ beginningEnd: null, currentLimit: '' }],
  );

  const addInterval = () => {
    setIntervals([...intervals, { beginningEnd: null, currentLimit: '' }]);
  };

  const removeInterval = (indexToRemove) => {
    setIntervals((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  useEffect(() => {
    setIsIntervalValid(H.validateIntervals(intervals));
  }, [intervals]);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={(values) => {
        const errors = {};
        if (!values.description) {
          errors.description = T('Description') + ' ' + T('is required');
        }

        if (!values.currentLimitDefault) {
          errors.currentLimitDefault =
            T('Current Limit (A)') + ' ' + T('is required');
        }

        intervals.forEach((interval, index) => {
          if (!interval.currentLimit) {
            if (!errors.intervals) errors.intervals = [];
            errors.intervals[index] = {
              currentLimit: T('Current Limit (A)') + ' ' + T('is required'),
            };
          } else if (
            parseInt(interval.currentLimit) ===
            parseInt(values.currentLimitDefault)
          ) {
            if (!errors.intervals) errors.intervals = [];
            errors.intervals[index] = {
              ...errors.intervals[index],
              currentLimit: T(
                'Current Limit must be different from Default Limit',
              ),
            };
          }

          if (!interval.beginningEnd || interval.beginningEnd.length < 2) {
            if (!errors.intervals) errors.intervals = [];
            errors.intervals[index] = {
              ...errors.intervals[index],
              beginningEnd: T('Time interval is required'),
            };
          }
        });

        return errors;
      }}
      onSubmit={(values, { setSubmitting }) => {
        values.intervals = intervals;
        handleSubmit(values, { setSubmitting }, type, id, history);
      }}
    >
      {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => {
        return (
          <S.ChargingProfileFormWrapper>
            <form onSubmit={handleSubmit}>
              <S.FormInputGroupWrapper>
                <S.FormInputWrapper>
                  <S.FormLabel>{T('Description')}*:</S.FormLabel>
                  <Input
                    name='description'
                    label='description'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.description}
                    maxLength={20}
                    allowClear
                    required
                  />
                  <ErrorMessage name='description' component={S.MessageBox} />
                </S.FormInputWrapper>

                <S.FormInputWrapper>
                  <S.FormLabel>{T('Current Limit (A)')} Default*:</S.FormLabel>
                  <Input
                    name='currentLimitDefault'
                    label='currentLimitDefault'
                    type='number'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.currentLimitDefault}
                    maxLength={20}
                    allowClear
                    required
                  />
                  <ErrorMessage
                    name='currentLimitDefault'
                    component={S.MessageBox}
                  />
                </S.FormInputWrapper>
              </S.FormInputGroupWrapper>

              <S.FormInputWrapper>
                <Button
                  type='button'
                  onClick={addInterval}
                  className='intervals-input'
                >
                  +
                </Button>
                <S.FormLabel className='intervals-label'>
                  {T('Intervals')}
                </S.FormLabel>
              </S.FormInputWrapper>

              {intervals.map((interval, index) => (
                <S.FormInputGroupWrapper key={index}>
                  <S.FormInputWrapper>
                    {index !== 0 && (
                      <Button
                        type='button'
                        onClick={() => removeInterval(index)}
                        className='intervals-input'
                        style={{
                          width: '18px',
                          height: '18px',
                          minWidth: '20px',
                          marginLeft: '1px',
                          backgroundColor: S.lighterRed,
                          borderColor: S.lighterRed,
                          color: 'white',
                          padding: '0',
                          marginTop: '-18px',
                        }}
                      >
                        X
                      </Button>
                    )}

                    <S.FormLabel>
                      {T('Beginning')} - {T('End')}*:
                    </S.FormLabel>

                    <S.StyledTimePicker
                      name={`intervals[${index}].beginningEnd`}
                      label={`intervals[${index}].beginningEnd`}
                      format='HH:mm'
                      onChange={(dates) => {
                        const newIntervals = [...intervals];

                        newIntervals[index].beginningEnd = dates.map((date) =>
                          date.format('HH:mm'),
                        );

                        let isIntervalValid = H.validateIntervals(newIntervals);
                        setIsIntervalValid(isIntervalValid);

                        if (!isIntervalValid) {
                          message.error(
                            T(
                              'Intervals cannot overlap and must be in chronological order',
                            ),
                          );
                        }

                        setIntervals(newIntervals);
                      }}
                      onBlur={handleBlur}
                      value={interval.beginningEnd?.map((time) =>
                        typeof time === 'string' ? moment(time, 'HH:mm') : time,
                      )}
                    />
                    <ErrorMessage
                      name={`intervals[${index}].beginningEnd`}
                      component={S.MessageBox}
                    />
                  </S.FormInputWrapper>

                  <S.FormInputWrapper>
                    <S.FormLabel>{T('Current Limit (A)')}*:</S.FormLabel>
                    <Input
                      name={`intervals[${index}].currentLimit`}
                      label={`intervals[${index}].currentLimit`}
                      onChange={(e) => {
                        const newIntervals = [...intervals];
                        newIntervals[index].currentLimit = e.target.value;
                        setIntervals(newIntervals);
                      }}
                      onBlur={handleBlur}
                      value={interval.currentLimit}
                      maxLength={20}
                      type='number'
                      allowClear
                      required
                    />
                    <ErrorMessage
                      name={`intervals[${index}].currentLimit`}
                      component={S.MessageBox}
                    />
                  </S.FormInputWrapper>
                </S.FormInputGroupWrapper>
              ))}

              <Button
                type='primary'
                disabled={
                  isSubmitting ||
                  !isIntervalValid ||
                  !values.description ||
                  !values.currentLimitDefault ||
                  intervals.some(
                    (interval) =>
                      !interval.currentLimit ||
                      !interval.beginningEnd ||
                      interval.beginningEnd.length < 2 ||
                      parseInt(interval.currentLimit) ===
                        parseInt(values.currentLimitDefault),
                  )
                }
                onClick={handleSubmit}
              >
                {T('Submit')}
              </Button>
            </form>
          </S.ChargingProfileFormWrapper>
        );
      }}
    </Formik>
  );
};

export default ChargingProfileForm;
